import type {
	LayerFilter, PopupConfig, QueryFeature
} from '@/components/mapbox/legacy-types'
import type { FilterConflict } from '@accounts/types'
import type {
	DrawCreateEvent, DrawDeleteEvent, DrawUpdateEvent
} from '@mapbox/mapbox-gl-draw'
import type {
	Feature, GeoJsonProperties, Geometry
} from 'geojson'
import type { GeoJSONFeature, Map as MapboxMap } from 'mapbox-gl'

interface MapState {
	mapInstance: MapboxMap | null
	isLoaded: boolean
	isStyleLoaded: boolean
	activePolygonId: string
	activePolygonLabel: string
	activeMarkerId: string
	activeSourceId: string
	popup: PopupConfig | null
	layerFilters: LayerFilter[]
	lastClickedFeatures: QueryFeature[] | undefined
	lastHoveredFeatures: QueryFeature[] | undefined
	lassoFeatures: Feature<Geometry, GeoJsonProperties>[]
	lastLassoFeatures: Feature<Geometry, GeoJsonProperties>[]
	lastDrawPolygon: any
	lastDrawArea: number | null
	filterConflicts: FilterConflict[]
}

// Create an empty map state
function createEmptyMapState(): MapState {
	return {
		mapInstance: null,
		isLoaded: false,
		isStyleLoaded: false,
		activePolygonId: '',
		activePolygonLabel: '',
		activeMarkerId: '',
		activeSourceId: '',
		popup: null,
		layerFilters: [],
		lastClickedFeatures: undefined,
		lastHoveredFeatures: undefined,
		lassoFeatures: [],
		lastLassoFeatures: [],
		lastDrawPolygon: null,
		lastDrawArea: null,
		filterConflicts: []
	}
}

// Global map state registry using Map instead of plain object
const mapStateRegistry = reactive(new Map<string, MapState>())

export function useMapboxState(mapId: string) {
	// Initialize map state if it doesn't exist
	if (!mapStateRegistry.has(mapId)) {
		mapStateRegistry.set(mapId, reactive(createEmptyMapState()))
	}

	// Get the specific map state
	const mapState = computed(() => mapStateRegistry.get(mapId)!)

	// Register a map instance
	const registerMap = (map: MapboxMap) => {
		// ts-error-ignore: type instantioning is excessively deep and possibly infinite.
		mapState.value.mapInstance = map as any // Explicitly cast to avoid type instantiation error
	}

	// Set map as loaded
	const setMapLoaded = () => {
		mapState.value.isLoaded = true
	}

	// Set map style as loaded
	const setStyleLoaded = () => {
		mapState.value.isStyleLoaded = true
	}

	// Handle map click events
	const handleMapClick = (features: GeoJSONFeature[] | undefined) => {
		// clear any existing active source ID so that we don't
		// zoom to the marker when clicking the map
		clearActiveSourceId()
		mapState.value.lastClickedFeatures = features as QueryFeature[] | undefined | any
	}

	// Handle map hover events
	const handleMapHover = (features: GeoJSONFeature[] | undefined) => {
		mapState.value.lastHoveredFeatures = features as QueryFeature[] | undefined | any
	}

	/**
	 * Set the active polygon ID to display only this polygon (zone)
	 */
	const setActivePolygonId = (id: string) => {
		mapState.value.activePolygonId = id
	}
	// Clear active polygon ID
	const clearActivePolygonId = () => {
		setActivePolygonId('')
	}

	/**
	 * Set the active polygon label to display in the popup
	 */
	const setActivePolygonLabel = (label: string) => {
		mapState.value.activePolygonLabel = label
	}
	// Clear active polygon label
	const clearActivePolygonLabel = () => {
		setActivePolygonLabel('')
	}

	// Clear active polygon
	const clearActivePolygon = () => {
		clearActivePolygonId()
		clearActivePolygonLabel()
	}

	/**
	 * Set the active marker ID to display the popup
	 */
	const setActiveMarkerId = (id: string) => {
		mapState.value.activeMarkerId = id
	}

	// Clear active marker ID
	const clearActiveMarkerId = () => {
		setActiveMarkerId('')
	}

	/**
	 * Set the active source ID to zoom to the marker coordinates
	 * when clicking the address from the table.
	 */
	const setActiveSourceId = (id: string) => {
		mapState.value.activeSourceId = id
	}

	// Clear active source ID
	const clearActiveSourceId = () => {
		setActiveSourceId('')
	}

	// Set popup
	const setPopupState = (popup: PopupConfig | null) => {
		if (!popup) {
			mapState.value.popup = null
			return
		}

		const defaultOffset = {
			left: [ 0, 0 ],
			right: [ -30, 0 ],
			top: [ 0, 0 ],
			bottom: [ 0, 0 ]
		} as PopupConfig['offset']
		const {
			coordinates, html, offset = defaultOffset
		} = popup

		mapState.value.popup = {
			coordinates,
			html,
			offset
		}
	}

	// Update layer filters
	const updateLayerFilters = (filters: LayerFilter[]) => {
		mapState.value.layerFilters = filters
	}

	// Add layer filter
	const addLayerFilter = (filter: LayerFilter) => {
		// Remove any existing filter with the same ID
		mapState.value.layerFilters = mapState.value.layerFilters.filter(
			f => f.filterId !== filter.filterId
		)
		// Add the new filter
		mapState.value.layerFilters.push(filter)
	}

	// Remove layer filter by ID
	const removeLayerFilter = (filterId: string) => {
		mapState.value.layerFilters = mapState.value.layerFilters.filter(
			filter => filter.filterId !== filterId
		)
	}

	// Handle lasso feature selection
	const setLassoFeatures = (features: Feature<Geometry, GeoJsonProperties>[]) => {
		mapState.value.lassoFeatures = features
	}

	// Clear lasso features
	const clearLassoFeatures = () => {
		mapState.value.lastLassoFeatures = mapState.value.lassoFeatures
		mapState.value.lassoFeatures = []
	}

	// Handle draw create
	const handleDrawCreate = (polygon: any, _event: DrawCreateEvent) => { // TODO: event not used
		mapState.value.lastDrawPolygon = polygon
	}

	// Handle draw update
	const handleDrawUpdate = (
		polygon: any,
		area: number | null,
		features: Feature<Geometry, GeoJsonProperties>[],
		_event: DrawUpdateEvent | DrawDeleteEvent // TODO: event not used
	) => {
		mapState.value.lastDrawPolygon = polygon
		mapState.value.lastDrawArea = area
		mapState.value.lassoFeatures = features
		mapState.value.lastLassoFeatures = features
	}

	const setFilterConflicts = (conflicts: FilterConflict[]) => {
		mapState.value.filterConflicts = conflicts
	}

	return {
		mapState,
		lassoActive: computed(() => mapState.value.lassoFeatures.length > 0),
		registerMap,
		setMapLoaded,
		setStyleLoaded,
		handleMapClick,
		handleMapHover,
		setActivePolygonId,
		clearActivePolygon, // Mapbox control callback for reset button
		clearActivePolygonId,
		setActivePolygonLabel,
		clearActivePolygonLabel,
		setActiveMarkerId,
		clearActiveMarkerId,
		setActiveSourceId,
		clearActiveSourceId,
		setPopupState,
		updateLayerFilters,
		addLayerFilter,
		removeLayerFilter,
		setLassoFeatures,
		clearLassoFeatures,
		handleDrawCreate,
		handleDrawUpdate,
		setFilterConflicts
	}
}
